import isDate from 'lodash/isDate';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import intlInstanceCache from './intlInstanceCache';
import { FormatterOptions } from '../models/configuration/Formatter';
import FormatNumberOptions from '../models/utils/FormatNumberOptions';
import FormatDateOpitons from '../models/utils/FormatDateOptions';
import { NumberThresholds } from '../models/configuration/NumberThresholds';
import { sortBy } from '@kurtosys/ksys-app-template/dist/utils/collection';
import {
    isNullOrUndefined,
    isNullOrEmpty,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { translate } from '../Components/Translate';

export const padStart = (value: any, length: number, padder: string = '0') => {
    if (value !== undefined && value !== null) {
        let response: string = value.toString();
        if (response.length < length) {
            const split = response.split('');
            while (split.length < length) {
                split.unshift(padder);
            }
            response = split.join('');
        }
        return response;
    }
    return '';
};

export const formatNumber = (formatNumberOptions: FormatNumberOptions) => {
    let { value } = formatNumberOptions;
    const { locale, options, dataStore } = formatNumberOptions;

    if (isNumber(value)) {
        try {
            const {
                numberThresholds,
                thresholdSeparator = '',
                mustDividePercent = false,
            } = options;
            if (mustDividePercent) {
                value = value / 100;
            }
            let numberThreshold: NumberThresholds | undefined;
            if (numberThresholds) {
                const sortedNumberThresholds = sortBy(
                    numberThresholds,
                    item => item.value.toString(),
                    'DESC'
                );
                for (const numberGroup of sortedNumberThresholds) {
                    const { value: thresholdValue, text } = numberGroup;
                    // if there is no text placeholder, we need to adjust every value
                    if (
                        isNullOrEmpty(text) ||
                        Math.abs(value) >= thresholdValue
                    ) {
                        numberThreshold = numberGroup;
                        value = value / thresholdValue;
                        break;
                    }
                }
            }
            const formattedNumber = intlInstanceCache
                .getNumber({ locale, options, dataStore })
                .format(value);
            if (numberThreshold) {
                const {
                    text,
                    separator = thresholdSeparator,
                    displayPosition = 'suffix',
                } = numberThreshold;
                if (text) {
                    const translatedText =
                        (translate &&
                            translate(dataStore.stores.translation, text)) ||
                        text;

                    if (displayPosition === 'suffix') {
                        return `${formattedNumber}${separator}${translatedText}`;
                    }
                    if (displayPosition === 'prefix') {
                        return `${translatedText}${separator}${formattedNumber}`;
                    }
                }
            }
            return formattedNumber;
        } catch (e) {
            console.warn(e);
            return value;
        }
    }
    return value;
};

export const formatDateTime = (formatDateOptions: FormatDateOpitons) => {
    let { value } = formatDateOptions;
    const { locale, options, dataStore } = formatDateOptions;
    // Cleans up dates coming in with a format of YYYY-MM-DD HH:mm:SS, it will change them to
    // YYYY-MM-DDTHH:mm:SS. This is required for how IE11 handles value format for the new Date(value).
    const dateRegex = new RegExp(
        /(\d){4}-(\d){2}-(\d){2} (\d){2}\:(\d){2}\:(\d){2}/
    );
    if (typeof value === 'string' && dateRegex.test(value)) {
        value = value.replace(' ', 'T');
    }

    if (
        typeof value === 'string' &&
        /^(\d){4}-(\d){2}-(\d){2}T(\d){2}\:(\d){2}\:(\d){2}$/.test(value)
    ) {
        value += '.000Z';
    }

    const date = new Date(value);
    const isValidDate = isDate(date) && !isNaN(date.getTime());

    const dateTimeOptions: Intl.DateTimeFormatOptions = {
        timeZone: 'UTC',
        ...options,
    };

    if (isValidDate) {
        try {
            return intlInstanceCache
                .getDateTime({ locale, dataStore, options: dateTimeOptions })
                .format(date);
        } catch (e) {
            console.warn(e);
            return value;
        }
    }

    return value;
};

const formatter = (formatOptions: FormatterOptions) => {
    const {
        type,
        locale,
        options,
        value,
        currency,
        dataStore,
        format = {},
    } = formatOptions;
    const { id } = format;
    if (id) {
        const globalFormats =
            dataStore &&
            dataStore.config &&
            dataStore.config.globalConfig &&
            dataStore.config.globalConfig.formats;
        if (globalFormats) {
            const globalFormat = globalFormats[id];
            if (globalFormat) {
                const {
                    optionsByCulture,
                    options: globalOptions,
                } = globalFormat;
                let appliedGlobalOptions = { ...globalOptions };

                if (locale && optionsByCulture && optionsByCulture[locale]) {
                    appliedGlobalOptions = { ...optionsByCulture[locale] };
                }

                return formatter({
                    locale,
                    currency,
                    dataStore,
                    value,
                    format: {}, // to prevent possible recursion loops, pass empty obj
                    type: globalFormat.type,
                    options: appliedGlobalOptions,
                });
            }
        }
    }
    if (type === 'number') {
        return formatNumber({
            locale,
            options,
            dataStore,
            value: value as number,
        });
    }

    if (type === 'currency') {
        (options as Intl.NumberFormatOptions).currency = currency;
        return formatNumber({
            locale,
            options,
            dataStore,
            value: value as number,
        });
    }

    if (type === 'date') {
        return formatDateTime({
            locale,
            options,
            dataStore,
            value: !isNullOrUndefined(value) && value,
        } as FormatDateOpitons);
    }

    return value;
};

export default formatter;



// WEBPACK FOOTER //
// ./src/utils/formatter.ts